import React, { useEffect, useRef, useState } from 'react';
import { View, Text, Dimensions, StyleSheet, Animated, TouchableOpacity, Modal, ScrollView } from 'react-native';
import { BarChart } from 'react-native-chart-kit';
import Svg, { Circle, Path } from 'react-native-svg';
import { IssuerData } from '../types';
import { CHART_COLORS } from '../constants/theme';

interface ChartComponentProps {
  data: IssuerData[];
  chartType: 'pie' | 'bar';
  variant?: 'card' | 'embedded';
  scale?: number;
  showAllData?: boolean;
  showAllInChart?: boolean;
  maxItems?: number;
  minPercentage?: number;
  groupSmallItems?: boolean;
  metric?: 'products' | 'notional' | 'percentage';
  onIssuerPress?: (issuerName: string) => void;
  hideLegendDetails?: boolean;
  centerLabel?: string; // Custom label for center of donut chart
  showLegendAmountBelow?: boolean; // Show monetary amount stacked under %
  currencyCode?: string;
}

const screenWidth = Dimensions.get('window').width;
const isDesktop = screenWidth >= 768;
const isDesktopWide = screenWidth >= 1280;
const maxIssuerNameLen = isDesktopWide ? 20 : 10;

export const ChartComponent: React.FC<ChartComponentProps> = ({
  data,
  chartType,
  variant = 'card',
  scale = 1,
  showAllData = false,
  showAllInChart = false,
  maxItems = 5,
  minPercentage = 2,
  groupSmallItems = true,
  metric = 'products',
  onIssuerPress,
  hideLegendDetails = false,
  centerLabel,
  showLegendAmountBelow = false,
  currencyCode = 'CHF',
}) => {
  if (!data || !Array.isArray(data) || data.length === 0) {
    return (
      <View style={styles.container}>
        <Text style={styles.headerTitle}>No data available</Text>
        <Text style={styles.subtitle}>Please try again later</Text>
      </View>
    );
  }

  const processDataForChart = (rawData: IssuerData[]) => {
    if (!groupSmallItems || rawData.length <= 3) {
      return rawData;
    }

    const sortedData = [...rawData].sort((a, b) => b.allocation - a.allocation);

    let finalData: IssuerData[] = [];
    let remainingData: IssuerData[] = [];

    // Para pie charts, por defecto limitar a 4, pero si maxItems es mayor que 4, respetarlo (asumimos que fue pasado explícitamente)
    const effectiveMaxItems = chartType === 'pie' 
      ? (maxItems > 4 ? maxItems : Math.min(maxItems, 4))
      : maxItems;
    const effectiveMinPercentage = chartType === 'pie' ? Math.max(minPercentage, 3) : minPercentage;

    finalData = sortedData.slice(0, effectiveMaxItems);
    remainingData = sortedData.slice(effectiveMaxItems);

    // Solo filtrar por porcentaje mínimo si maxItems es el default (4 o menos para pie charts)
    // Si maxItems es mayor (como 9), mostrar todos los items principales sin filtrar por porcentaje
    if (effectiveMaxItems <= 4) {
      const verySmallItems = finalData.filter(item => item.allocation < effectiveMinPercentage);
      if (verySmallItems.length > 0) {
        finalData = finalData.filter(item => item.allocation >= effectiveMinPercentage);
        remainingData = [...remainingData, ...verySmallItems];
      }
    }

    if (remainingData.length > 0) {
      const othersTotal = remainingData.reduce((sum, item) => sum + item.numberOfProducts, 0);
      const othersOriginalTotal = remainingData.reduce(
        (sum, item) => sum + (item.notionalOriginal ?? item.numberOfProducts),
        0
      );
      const othersPercentage = remainingData.reduce((sum, item) => sum + item.allocation, 0);

      finalData.push({
        issuerName: `Others (${remainingData.length})`,
        numberOfProducts: othersTotal,
        notionalOriginal: othersOriginalTotal,
        allocation: parseFloat(othersPercentage.toFixed(2)),
      });
    }

    if (finalData.length > effectiveMaxItems) {
      const excess = finalData.slice(effectiveMaxItems - 1);
      finalData = finalData.slice(0, effectiveMaxItems - 1);
      
      const excessTotal = excess.reduce((sum, item) => sum + item.numberOfProducts, 0);
      const excessPercentage = excess.reduce((sum, item) => sum + item.allocation, 0);
      
      // Correctly calculate the number of issuers in "Others"
      const excessCount = excess.reduce((count, item) => {
        if (item.issuerName.startsWith('Others (')) {
          const match = item.issuerName.match(/Others \((\d+)\)/);
          return count + (match ? parseInt(match[1], 10) : 0);
        }
        return count + 1;
      }, 0);

      finalData.push({
        issuerName: `Others (${excessCount})`,
        numberOfProducts: excessTotal,
        notionalOriginal: excess.reduce(
          (sum, item) => sum + (item.notionalOriginal ?? item.numberOfProducts),
          0
        ),
        allocation: parseFloat(excessPercentage.toFixed(2)),
      });
    }

    return finalData;
  };

  const processedData = processDataForChart(data);
  const limitedData = showAllData ? data : processedData;
  const chartDataForRender = showAllInChart ? data : processedData;





  const palette = CHART_COLORS;

  const barData = {
    labels: limitedData.map((item) =>
      item.issuerName.length > maxIssuerNameLen ? item.issuerName.substring(0, maxIssuerNameLen) + '...' : item.issuerName,
    ),
    datasets: [
      {
        data: limitedData.map((item) => item.numberOfProducts),
        color: (opacity = 1) => `rgba(0, 122, 255, ${opacity})`,
      },
    ],
  };

  const chartConfig = {
    backgroundColor: '#ffffff',
    backgroundGradientFrom: '#ffffff',
    backgroundGradientTo: '#ffffff',
    decimalPlaces: 0,
    color: (opacity = 1) => `rgba(0, 0, 0, ${opacity})`,
    labelColor: (opacity = 1) => `rgba(0, 0, 0, ${opacity})`,
    style: {
      borderRadius: 16,
    },
    propsForDots: {
      r: '6',
      strokeWidth: '2',
      stroke: '#ffa726',
    },
  };

  if (chartType === 'pie') {
    const totalProducts = chartDataForRender.reduce((sum, i) => sum + i.numberOfProducts, 0);
    const wrapperStyle =
      variant === 'card'
        ? [styles.container, screenWidth <= 360 && styles.container320]
        : styles.embeddedContainer;
    const breakpoints = {
      xs: 320, // Very small
      sm: 360, // Small (Android standard)
      md: 375, // Medium (iPhone standard)
      lg: 414, // Grande (iPhone Plus)
      xl: 768, // Tablet
    };
  
    const getChartSize = (width: number) => {
      if (width < breakpoints.sm) return { radius: 55, innerRadius: 40 }; // xs
      if (width < breakpoints.md) return { radius: 60, innerRadius: 44 }; // sm
      if (width < breakpoints.lg) return { radius: 70, innerRadius: 51 }; // md
      if (width < breakpoints.xl) return { radius: 75, innerRadius: 55 }; // lg
      if (width < 1440) return { radius: 85, innerRadius: 62 }; // Laptop (1024-1440px)
      return { radius: 110, innerRadius: 80 }; // Laptop L (1440px+)
    };

    const { radius, innerRadius } = getChartSize(screenWidth);
    const [displayTotal, setDisplayTotal] = useState(0);
    const animOpacity = useRef(new Animated.Value(0)).current;
    const animScale = useRef(new Animated.Value(0.95)).current;
    const [progress, setProgress] = useState(0);
    const [showOthersModal, setShowOthersModal] = useState(false);

    // Get the "Others" data for the modal
    const getOthersData = (): IssuerData[] => {
      if (!groupSmallItems || data.length <= 3) return [];
      const sortedData = [...data].sort((a, b) => b.allocation - a.allocation);

      // Match logic with processDataForChart
      const effectiveMaxItems = chartType === 'pie' ? (maxItems > 4 ? maxItems : Math.min(maxItems, 4)) : maxItems;
      const effectiveMinPercentage = chartType === 'pie' ? Math.max(minPercentage, 3) : minPercentage;
      
      let mainItems = sortedData.slice(0, effectiveMaxItems - 1);
      let remainingData = sortedData.slice(effectiveMaxItems - 1);
      
      if (effectiveMaxItems <= 4) {
        const verySmallItems = mainItems.filter(item => item.allocation < effectiveMinPercentage);
        if (verySmallItems.length > 0) {
          mainItems = mainItems.filter(item => item.allocation >= effectiveMinPercentage);
          remainingData = [...remainingData, ...verySmallItems];
        }
      }
      
      return remainingData;
    };

    const othersData = getOthersData();

    useEffect(() => {
      Animated.parallel([
        Animated.timing(animOpacity, { toValue: 1, duration: 450, useNativeDriver: true }),
        Animated.spring(animScale, { toValue: 1, friction: 6, useNativeDriver: true }),
      ]).start();

      const duration = 600;
      const steps = 30;
      const stepTime = duration / steps;
      let current = 0;
      const increment = totalProducts / steps;
      const interval = setInterval(() => {
        current += increment;
        if (current >= totalProducts) {
          current = totalProducts;
          clearInterval(interval);
        }
        setDisplayTotal(Math.round(current));
      }, stepTime);

      let start: number | null = null;
      const sweepDuration = 900;
      const raf = (t: number) => {
        if (start === null) start = t;
        const elapsed = t - start;
        const p = Math.min(1, elapsed / sweepDuration);
        setProgress(p);
        if (p < 1) requestAnimationFrame(raf);
      };
      requestAnimationFrame(raf);
      return () => clearInterval(interval);
    }, [totalProducts]);
    return (
      <View style={wrapperStyle}>
        {variant === 'card' && (
          <View style={styles.headerRow}>
            <Text style={styles.headerTitle}>Issuer ranking</Text>
            <View style={styles.segmented}>
              <View style={[styles.segment, styles.segmentActive]}>
                <Text style={[styles.segmentText, styles.segmentTextActive]}>Products</Text>
              </View>
              <View style={styles.segment}>
                <Text style={styles.segmentText}>Notional</Text>
              </View>
            </View>
          </View>
        )}
        <View style={[styles.row, screenWidth < breakpoints.md && styles.rowStack]}>
          <View style={styles.leftCol}>
            <Animated.View
              style={[
                styles.donutWrapper,
                {
                  width: radius * 2,
                  height: radius * 2,
                  transform: [{ scale: animScale }, { scale }],
                  opacity: animOpacity,
                  alignSelf: 'center',
                },
              ]}
            >
              <Donut
                percentageItems={chartDataForRender
                  .filter(item => item.allocation >= 0.5)
                  .map((item, i) => ({
                    value: item.numberOfProducts,
                    color: palette[i % palette.length],
                  }))}
                size={radius * 2}
                strokeWidth={radius - innerRadius}
                progress={progress}
              />
              <View
                style={[
                  styles.donutCenter,
                  {
                    width: innerRadius * 2 - 4,
                    height: innerRadius * 2 - 4,
                    borderRadius: innerRadius - 2,
                  },
                ]}
              >
                <Text style={[
                  styles.centerValue,
                  { fontSize: metric === 'notional' || metric === 'percentage' ? Math.round(radius * 0.22) : Math.round(radius * 0.44) }
                ]}>
                  {centerLabel 
                    ? centerLabel 
                    : metric === 'notional' 
                      ? formatMoneyShort(displayTotal, currencyCode) 
                      : metric === 'percentage'
                        ? '100%'
                        : displayTotal}
                </Text>
              </View>
            </Animated.View>
          </View>
          <View
            style={[
              styles.legend,
              screenWidth < breakpoints.md && { paddingLeft: 0, marginTop: 12 },
            ]}
          >
            {limitedData.map((item, index) => {
              const isOthersGroup = item.issuerName.startsWith('Others');
              const issuerNameBase =
                item.issuerName.length > maxIssuerNameLen && !isOthersGroup
                  ? item.issuerName.substring(0, maxIssuerNameLen) + '.'
                  : item.issuerName;
              const issuerLabel = metric === 'products' ? `${issuerNameBase} (${item.numberOfProducts})` : issuerNameBase;
              return (
                <TouchableOpacity
                  key={item.issuerName + index}
                  style={[styles.legendItem, screenWidth < breakpoints.md && styles.legendItemSm]}
                  onPress={() => {
                    if (isOthersGroup) {
                      setShowOthersModal(true);
                    } else {
                      onIssuerPress?.(item.issuerName);
                    }
                  }}
                  activeOpacity={0.7}
                >
                  <View
                    style={[
                      styles.legendDot,
                      { backgroundColor: palette[index % palette.length] },
                      isOthersGroup && styles.legendDotOthers,
                    ]}
                  />
                {showLegendAmountBelow ? (
                  <View style={styles.legendValueColumn}>
                    <Text
                      style={[
                        styles.legendPercent,
                        screenWidth < breakpoints.md && styles.legendPercentSm,
                      ]}
                    >
                      {item.allocation.toFixed(2)}%
                    </Text>
                    <Text
                      style={[
                        styles.legendAmount,
                        screenWidth < breakpoints.md && styles.legendAmountSm,
                      ]}
                    >
                      {metric === 'percentage'
                        ? formatNumberShort(item.notionalOriginal ?? item.numberOfProducts)
                        : metric === 'notional'
                      ? formatMoneyShort(item.numberOfProducts, currencyCode)
                          : `${item.numberOfProducts} product${item.numberOfProducts !== 1 ? 's' : ''}`}
                    </Text>
                  </View>
                ) : (
                  <Text
                    style={[
                      styles.legendPercent,
                      screenWidth < breakpoints.md && styles.legendPercentSm,
                    ]}
                  >
                    {item.allocation.toFixed(2)}%
                  </Text>
                )}
                  <Text
                    style={[
                      styles.legendLabel,
                      screenWidth < breakpoints.md && styles.legendLabelSm,
                      isOthersGroup && styles.legendLabelOthers,
                    ]}
                    numberOfLines={2}
                    ellipsizeMode="tail"
                  >
                    {issuerLabel}
                  </Text>
                {screenWidth >= breakpoints.xl && !hideLegendDetails && !showLegendAmountBelow && (
                    <Text
                      style={[
                        styles.legendProducts,
                        screenWidth < breakpoints.md && styles.legendProductsSm,
                        isOthersGroup && styles.legendProductsOthers,
                      ]}
                    >
                      {metric === 'notional'
                        ? formatMoneyShort(item.numberOfProducts, currencyCode)
                        : metric === 'percentage'
                          ? formatMoneyShort(item.notionalOriginal ?? item.numberOfProducts, currencyCode)
                          : `${item.numberOfProducts} product${item.numberOfProducts !== 1 ? 's' : ''}`}
                    </Text>
                  )}
                </TouchableOpacity>
              );
            })}
          </View>
        </View>

        {/* Others Detail Modal */}
        <Modal
          visible={showOthersModal}
          transparent
          animationType="slide"
          onRequestClose={() => setShowOthersModal(false)}
        >
          <View style={styles.othersModalOverlay}>
            <View style={styles.othersModalContent}>
              <View style={styles.othersModalHeader}>
                <Text style={styles.othersModalTitle}>Others Detail</Text>
                <TouchableOpacity 
                  onPress={() => setShowOthersModal(false)} 
                  style={styles.othersModalCloseButton}
                >
                  <Text style={styles.othersModalCloseText}>✕</Text>
                </TouchableOpacity>
              </View>
              <ScrollView style={styles.othersModalScrollView}>
                {othersData.map((item, idx) => (
                  <TouchableOpacity
                    key={`others-${item.issuerName}-${idx}`}
                    style={[
                      styles.othersModalItem,
                      { backgroundColor: idx % 2 === 0 ? '#fff' : '#F9FAFB' },
                    ]}
                    onPress={() => {
                      setShowOthersModal(false);
                      onIssuerPress?.(item.issuerName);
                    }}
                    activeOpacity={0.7}
                  >
                    <Text style={styles.othersModalItemName}>{item.issuerName}</Text>
                    <View style={styles.othersModalItemStats}>
                      <Text style={styles.othersModalItemProducts}>
                        {metric === 'notional'
                            ? formatMoneyShort(item.numberOfProducts, currencyCode)
                          : metric === 'percentage'
                              ? formatMoneyShort(item.notionalOriginal ?? item.numberOfProducts, currencyCode)
                            : `${item.numberOfProducts} product${item.numberOfProducts !== 1 ? 's' : ''}`}
                      </Text>
                      <Text style={styles.othersModalItemPercent}>{item.allocation.toFixed(2)}%</Text>
                    </View>
                  </TouchableOpacity>
                ))}
              </ScrollView>
            </View>
          </View>
        </Modal>
      </View>
    );
  }

  return (
    <View style={variant === 'card' ? styles.container : styles.embeddedContainer}>
      {variant === 'card' && <Text style={styles.headerTitle}>Product Quantity by Issuer</Text>}
      <BarChart
        data={barData}
        width={screenWidth - 40}
        height={220}
        chartConfig={chartConfig}
        verticalLabelRotation={45}
        fromZero
        showBarTops
        showValuesOnTopOfBars
        yAxisLabel=""
        yAxisSuffix=""
      />
    </View>
  );
};

interface DonutItem {
  value: number;
  color: string;
}

// Utility functions to draw arcs in SVG (compatible iOS/Android/Web)
const toRadians = (deg: number) => (deg * Math.PI) / 180;
const polarToCartesian = (cx: number, cy: number, r: number, angleDeg: number) => {
  const angleRad = toRadians(angleDeg - 90);
  return { x: cx + r * Math.cos(angleRad), y: cy + r * Math.sin(angleRad) };
};
const describeArc = (cx: number, cy: number, r: number, startAngle: number, endAngle: number) => {
  const start = polarToCartesian(cx, cy, r, endAngle);
  const end = polarToCartesian(cx, cy, r, startAngle);
  const largeArcFlag = endAngle - startAngle <= 180 ? 0 : 1;
  return `M ${start.x} ${start.y} A ${r} ${r} 0 ${largeArcFlag} 0 ${end.x} ${end.y}`;
};

const Donut: React.FC<{
  percentageItems: DonutItem[];
  size?: number;
  strokeWidth?: number;
  progress?: number;
}> = ({ percentageItems, size = 220, strokeWidth = 30, progress = 1 }) => {
  const radius = (size - strokeWidth) / 2;
  const total = percentageItems.reduce((s, it) => s + it.value, 0) || 1;

  const baseGap = Math.max(1, Math.min(strokeWidth * 0.08, 4));
  const gapLen = baseGap; 
  const gapAngleDeg = (gapLen / (2 * Math.PI * radius)) * 360;
  const effectiveGap = percentageItems.length > 1 ? gapAngleDeg : 0;
  let currentAngle = 0;

  return (
    <Svg width={size} height={size}>
      <Circle
        cx={size / 2}
        cy={size / 2}
        r={radius}
        stroke="#FFFFFF"
        strokeWidth={strokeWidth}
        fill="none"
      />
      {percentageItems.length === 1 && progress >= 0.999 ? (
        <Circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          stroke={percentageItems[0].color}
          strokeWidth={strokeWidth}
          fill="none"
        />
      ) : (
        percentageItems.map((item, index) => {
          const span = (item.value / total) * 360;
          const spanVisible = Math.max(0, span - effectiveGap);
          const startAngle = currentAngle;
          const endAngle = startAngle + spanVisible * progress;
          const d = describeArc(size / 2, size / 2, radius, startAngle, endAngle);

          currentAngle += span;
          
          return (
            <Path
              key={`arc-${index}`}
              d={d}
              stroke={item.color}
              strokeWidth={strokeWidth}
              strokeLinecap="butt"
              fill="none"
            />
          );
        })
      )}
    </Svg>
  );
};
function formatNumberShort(value: number) {
  if (value >= 1e12) return (value / 1e12).toFixed(1) + ' B';
  if (value >= 1e9)  return (value / 1e9).toFixed(1) + ' M';
  if (value >= 1e6)  return (value / 1e6).toFixed(1) + ' m';
  if (value >= 1e3)  return (value / 1e3).toFixed(1) + ' k';
  return value.toString();
}

function formatMoneyShort(value: number, currencyCode: string = 'CHF') {
  if (value >= 1e12) return `${currencyCode} ${(value / 1e12).toFixed(1)} B`;
  if (value >= 1e9)  return `${currencyCode} ${(value / 1e9).toFixed(1)} M`;
  if (value >= 1e6)  return `${currencyCode} ${(value / 1e6).toFixed(1)} m`;
  if (value >= 1e3)  return `${currencyCode} ${(value / 1e3).toFixed(1)} k`;
  return `${currencyCode} ${value}`;
}

const styles = StyleSheet.create({
  container: {
    backgroundColor: '#ffffff',
    borderRadius: 10,
    borderWidth: 1,
    borderColor: '#F4F4F4',
    padding: 20,
    margin: 20,
    shadowColor: '#000',
    shadowOffset: {
      width: 0,
      height: 2,
    },
    shadowOpacity: 0.25,
    shadowRadius: 3.84,
    elevation: 5,
  },
  container320: {
    padding: 12,
    margin: 12,
  },
  embeddedContainer: {
    backgroundColor: 'transparent',
    padding: 0,
    margin: 0,
  },
  headerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 12,
  },
  headerTitle: {
    fontSize: 18,
    fontWeight: '700',
    color: '#1F2937',
    fontFamily: 'Montserrat_700Bold',
  },
  segmented: {
    flexDirection: 'row',
    backgroundColor: '#F3F4F6',
    borderRadius: 20,
    padding: 4,
  },
  segment: {
    paddingVertical: 6,
    paddingHorizontal: 12,
    borderRadius: 14,
  },
  segmentActive: {
    backgroundColor: '#fff',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.08,
    shadowRadius: 2,
    elevation: 1,
  },
  segmentText: {
    fontSize: 12,
    color: '#6B7280',
    fontWeight: '600',
    fontFamily: 'Montserrat_600SemiBold',
  },
  segmentTextActive: {
    color: '#0891b2',
  },
  subtitle: {
    fontSize: 14,
    color: '#666',
    textAlign: 'center',
    marginTop: 8,
    fontFamily: 'Montserrat_400Regular',
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: isDesktop ? 16 : 0,
  },
  leftCol: {
    alignItems: 'center',
    justifyContent: 'center',
  },
  rowStack: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
  },
  donutWrapper: {
    width: 192,
    height: 192,
    justifyContent: 'center',
    alignItems: 'center',
  },
  donutCenter: {
    position: 'absolute',
    width: 100,
    height: 100,
    borderRadius: 50,
    backgroundColor: '#ffffff',
    justifyContent: 'center',
    alignItems: 'center',
  },
  centerValue: {
    fontSize: 28,
    fontWeight: '700',
    color: '#333',
    fontFamily: 'Montserrat_700Bold',
  },
  legend: {
    flex: 1,
    paddingLeft: 4,
    minWidth: 280,
  },
  legendItem: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: isDesktop ? 18 : 14,
    width: '100%',
  },
  legendItemSm: {
    marginBottom: 8,
  },
  legendDot: {
    width: isDesktop ? 18 : 16,
    height: isDesktop ? 18 : 16,
    borderRadius: isDesktop ? 7 : 6,
    marginRight: isDesktop ? 12 : 8,
  },
  legendPercent: {
    minWidth: isDesktop ? 70 : 60,
    marginRight: isDesktop ? 12 : 8,
    color: '#11B5A4',
    fontWeight: '700',
    fontFamily: 'Montserrat_700Bold',
    fontSize: isDesktop ? 16 : 14,
  },
  legendPercentSm: {
    minWidth: 50,
    fontSize: 12,
  },
  legendLabel: {
    flex: 1,
    color: '#333',
    fontWeight: '600',
    fontFamily: 'Montserrat_600SemiBold',
    fontSize: isDesktop ? 16 : 14,
    marginRight: isDesktop ? 16 : 0,
  },
  legendLabelSm: {
    fontSize: 12,
  },
  legendDotOthers: {
    borderWidth: 1,
    borderColor: '#E5E7EB',
    borderStyle: 'dashed',
  },
  legendLabelOthers: {
    fontStyle: 'italic',
    color: '#6B7280',
  },
  legendProducts: {
    color: '#9CA3AF',
    fontWeight: '500',
    fontFamily: 'Montserrat_500Medium',
    fontSize: isDesktop ? 15 : 13,
    marginLeft: 'auto',
  },
  legendProductsSm: {
    fontSize: 11,
  },
  legendProductsOthers: {
    fontStyle: 'italic',
  },
  legendValueColumn: {
    minWidth: isDesktop ? 104 : 82,
  },
  legendAmount: {
    color: '#6B7280',
    fontWeight: '700',
    fontFamily: 'Montserrat_700Bold',
    fontSize: isDesktop ? 18 : 15,
    marginTop: 2,
  },
  legendAmountSm: {
    fontSize: 12,
  },
  // Others Modal Styles
  othersModalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  othersModalContent: {
    backgroundColor: 'white',
    borderRadius: 16,
    width: '90%',
    maxWidth: 500,
    maxHeight: '70%',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.25,
    shadowRadius: 8,
    elevation: 8,
  },
  othersModalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 20,
    borderBottomWidth: 1,
    borderBottomColor: '#E5E7EB',
  },
  othersModalTitle: {
    fontSize: 18,
    fontWeight: '700',
    color: '#1F2937',
    fontFamily: 'Montserrat_700Bold',
  },
  othersModalCloseButton: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: '#F3F4F6',
    justifyContent: 'center',
    alignItems: 'center',
  },
  othersModalCloseText: {
    fontSize: 16,
    color: '#6B7280',
  },
  othersModalScrollView: {
    padding: 16,
  },
  othersModalItem: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 14,
    paddingHorizontal: 16,
    borderRadius: 8,
  },
  othersModalItemName: {
    fontSize: 15,
    fontWeight: '600',
    color: '#1F2937',
    flex: 1,
    fontFamily: 'Montserrat_600SemiBold',
  },
  othersModalItemStats: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 16,
  },
  othersModalItemProducts: {
    fontSize: 14,
    color: '#6B7280',
    fontFamily: 'Montserrat_400Regular',
  },
  othersModalItemPercent: {
    fontSize: 14,
    fontWeight: '600',
    color: '#11B5A4',
    minWidth: 60,
    textAlign: 'right',
    fontFamily: 'Montserrat_600SemiBold',
  },
});
